import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

type SymbolPricesParams = {
  symbol: string;
  start_at: string;
  end_at: string;
  lang: Lang;
};

const symbolPricesSchema = z.object({
  data: z.array(
    z.object({
      date: z.string(),
      closePrice: z.number(),
    })
  ),
  message: z.string(),
});

export type SymbolPrice = z.infer<typeof symbolPricesSchema>["data"][number];

export const getSymbolPrices = async ({
  symbol,
  start_at,
  end_at,
  lang,
}: SymbolPricesParams): Promise<SymbolPrice[]> => {
  const endpoint = `/symbol/price/${symbol}/${start_at}/${end_at}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return symbolPricesSchema.parse(await res.json()).data;
};

export const useSymbolPrices = ({
  symbol,
  start_at,
  end_at,
}: {
  symbol: string;
  start_at?: string;
  end_at?: string;
}) => {
  const { lang } = useLang();
  return useQuery(
    ["symbol-prices", symbol, start_at, end_at, lang],
    () =>
      getSymbolPrices({
        symbol,
        start_at: start_at ?? "2007-01-01",
        end_at: end_at ?? dayjs().format("YYYY-MM-DD"),
        lang,
      }),
    {
      keepPreviousData: true,
      enabled: !!symbol,
      refetchOnWindowFocus: false,
    }
  );
};
